import React, { useEffect, useRef, useState } from "react";
import "./CicdExecution.css";
import { Alert, AlertTitle, Button, Collapse } from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { useTranslation } from "react-i18next";
import { transportPackageError } from "../utils/transportPackageErrors";
import { api } from "../utils/helpers";

export const executionOf = value => value?.execution || value?.meta?.execution;
export const executionFailed = value => ["failed", "partial", "unknown"].includes(executionOf(value)?.status || value?.status);
// Display status: a package with error details must read "failed" even when its
// execution never started (e.g. PROMOTION_FAILED before dispatch).
export const effectiveStatus = item => {
  const exec = executionOf(item)?.status;
  if (exec && executionFailed(item)) return exec;
  if (exec && (executionOf(item)?.errorDetails?.length || item?.error)) return "failed";
  return exec || item?.status;
};

export function cicdMonitorUrl({ spaceId, runId, operationId, packageId }) {
  if (!spaceId) return null;
  const params = new URLSearchParams({ spaceId });
  if (runId && !String(runId).startsWith("snapshot_")) params.set("runId", String(runId));
  if (operationId) params.set("operationId", String(operationId));
  if (packageId) params.set("packageId", String(packageId));
  return `/cicd-pipeline?${params}`;
}

export function CicdMonitorButton({ spaceId, runId, operationId, packageId, execution, ...props }) {
  const { t } = useTranslation("cicdruns");
  const href = cicdMonitorUrl({ spaceId, runId: runId || execution?.errorDetails?.find(error => error.runId)?.runId || execution?.runId,
    operationId: execution?.operationId || operationId, packageId: execution?.packageId || packageId });
  if (!href) return null;
  return <Button component="a" variant="outlined" size="small" {...props} href={href} target="_blank" rel="noopener noreferrer"
    endIcon={<OpenInNewRounded />}>{t("execution.openMonitor")}</Button>;
}

export function ExecutionStatus({ execution }) {
  const { t } = useTranslation("cicdruns");
  if (!execution) return null;
  const status = ["pending", "queued", "running", "completed", "partial", "failed", "unknown", "not_started"].includes(execution.status) ? execution.status : "unknown";
  return <span className={`cicd-execution-status cicd-execution-status--${status}`} role="status">{t(`execution.status.${status}`)}</span>;
}

// Lauflog eines Pakettransports (PACKAGE_DELETED_UPLOAD_FAILED): nur eine https-Adresse wird verlinkt.
const safeLogUrl = value => (typeof value === "string" && /^https:\/\/[^\s"'<>]+$/.test(value) ? value : null);

// --- Error aggregation helpers ---

/** Extract the transport phase from an ARTIFACT_FAILED message like "ArtifactId: deployment". */
const extractPhase = error => {
  if (error.code !== "ARTIFACT_FAILED" || typeof error.message !== "string") return null;
  const match = error.message.match(/:\s*(undeploy|upload|configuration|deployment)$/);
  return match ? match[1] : null;
};

/** Extract the artifact ID from an ARTIFACT_FAILED message. */
const extractArtifactId = error => {
  if (error.code !== "ARTIFACT_FAILED" || typeof error.message !== "string") return null;
  const match = error.message.match(/^(.+?):\s*(undeploy|upload|configuration|deployment)$/);
  return match ? match[1].trim() : null;
};

/** Aggregation key: code + phase for ARTIFACT_FAILED, full identity for others. */
const aggregationKey = error => {
  const phase = extractPhase(error);
  if (phase) return `ARTIFACT_FAILED:${phase}`;
  return JSON.stringify([error.code, error.message, error.runId, error.pid, error.jobName, error.stepName]);
};

/** Group errors by aggregation key, preserving order of first occurrence. */
export function aggregateErrors(errors) {
  const groups = new Map();
  for (const error of errors) {
    const key = aggregationKey(error);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(error);
  }
  return [...groups.values()];
}

// --- Error hint resolution (texts live in i18n/{de,en}/cicdruns.json under execution.hints.*) ---

/** Known hint keys; the i18n key is execution.hints.<hintKey>. */
const HINT_KEYS = new Set([
  "ARTIFACT_FAILED_deployment", "ARTIFACT_FAILED_upload", "ARTIFACT_FAILED_configuration", "ARTIFACT_FAILED_undeploy",
  "PACKAGE_TRANSPORT_APP_UNREACHABLE",
]);

/** Resolve the hint for a group of errors; returns the translated string or null. */
export function errorHint(errors, language, tFn) {
  if (!errors.length) return null;
  const first = errors[0];
  const phase = extractPhase(first);
  const hintKey = phase ? `ARTIFACT_FAILED_${phase}` : first.code;
  if (!HINT_KEYS.has(hintKey)) return null;
  if (tFn) {
    const result = tFn(`execution.hints.${hintKey}`, { defaultValue: "" });
    return result || null;
  }
  // Fallback without t(): load from i18n JSON directly (used in pure unit tests).
  try {
    const catalog = String(language || "de").toLowerCase().startsWith("en")
      ? require("../i18n/en/cicdruns.json") : require("../i18n/de/cicdruns.json");
    return catalog?.execution?.hints?.[hintKey] || null;
  } catch { return null; }
}

// --- Runtime error fetcher for ARTIFACT_FAILED deployment ---

function RuntimeErrors({ targetInstanceId, flowIds, t }) {
  const [state, setState] = useState({ loading: true, errors: null, fetchError: false });
  // Stable key: avoids re-fetching when parent re-renders with a new but content-equal array.
  const flowKey = flowIds.join("\n");
  const cache = useRef({ key: null, result: null });
  useEffect(() => {
    if (!targetInstanceId || !flowKey) { setState({ loading: false, errors: null, fetchError: false }); return; }
    const cacheKey = `${targetInstanceId}\0${flowKey}`;
    if (cache.current.key === cacheKey && cache.current.result) { setState(cache.current.result); return; }
    let cancelled = false;
    setState({ loading: true, errors: null, fetchError: false });
    const ids = flowKey.split("\n");
    (async () => {
      try {
        const result = await api(`/api/${encodeURIComponent(targetInstanceId)}/runtime-errors`, {
          method: "POST", body: JSON.stringify({ flowIds: ids }),
        });
        const next = { loading: false, errors: Array.isArray(result) ? result : null, fetchError: false };
        if (!cancelled) { cache.current = { key: cacheKey, result: next }; setState(next); }
      } catch {
        const next = { loading: false, errors: null, fetchError: true };
        if (!cancelled) { cache.current = { key: cacheKey, result: next }; setState(next); }
      }
    })();
    return () => { cancelled = true; };
  }, [targetInstanceId, flowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.loading) return <div className="cicd-runtime-loading" role="status" style={{ fontSize: 12, color: "var(--iio-text-3)", padding: "4px 0" }}>{t("execution.sapErrorsLoading")}</div>;
  if (state.fetchError) return <div style={{ fontSize: 12, color: "var(--iio-text-3)", padding: "4px 0" }}>{t("execution.sapErrorsUnavailable")}</div>;
  if (!state.errors?.length) return null;

  return <div className="cicd-runtime-errors">
    {state.errors.filter(e => e.flowId).map(entry => entry.error
      ? <div key={entry.flowId} style={{ padding: "2px 0", fontSize: 12 }}>
          <span style={{ fontFamily: "var(--iio-font-mono)", color: "var(--iio-text-2)" }}>{entry.flowId}</span>
          <span style={{ color: "var(--iio-text-3)", margin: "0 4px" }}>—</span>
          <span style={{ color: "var(--iio-text-3)" }}>{t("execution.sapErrorsUnavailable")}</span>
        </div>
      : <RuntimeErrorEntry key={entry.flowId} entry={entry} t={t} />)}
  </div>;
}

function RuntimeErrorEntry({ entry, t }) {
  const [expanded, setExpanded] = useState(false);
  const errorText = typeof entry.errorText === "string" ? entry.errorText.trim() : null;
  const long = errorText && errorText.length > 200;
  if (!errorText && !entry.status) return null;
  return <div style={{ padding: "2px 0", fontSize: 12 }}>
    <span style={{ fontFamily: "var(--iio-font-mono)", color: "var(--iio-text-2)" }}>{entry.flowId}</span>
    {entry.status && entry.status !== "STARTED" && <span style={{ color: "var(--iio-text-3)", marginLeft: 6 }}>{entry.status}</span>}
    {errorText && <>
      <span style={{ color: "var(--iio-text-3)", margin: "0 4px" }}>—</span>
      {long && !expanded
        ? <><span style={{ color: "var(--iio-text-1)" }}>{errorText.slice(0, 200)}...</span>
          <button type="button" onClick={() => setExpanded(true)}
            style={{ background: "none", border: "none", color: "var(--iio-accent)", cursor: "pointer", fontSize: 12, padding: "0 4px" }}>
            <ExpandMoreRoundedIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />{t("execution.sapErrorsExpand")}
          </button></>
        : <span style={{ color: "var(--iio-text-1)", overflowWrap: "anywhere" }}>{errorText}</span>}
      {long && expanded && <button type="button" onClick={() => setExpanded(false)}
        style={{ background: "none", border: "none", color: "var(--iio-accent)", cursor: "pointer", fontSize: 12, padding: "0 4px" }}>
        <ExpandLessRoundedIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />{t("execution.sapErrorsCollapse")}
      </button>}
    </>}
  </div>;
}

const errorPanelLayout = {
  mt: 1, overflowWrap: "anywhere", display: "grid",
  gridTemplateColumns: { xs: "auto minmax(0, 1fr)", sm: "auto minmax(0, 1fr) auto" },
  "& .MuiAlert-icon": { gridColumn: 1, gridRow: 1 },
  "& .MuiAlert-message": { gridColumn: 2, gridRow: 1, minWidth: 0 },
  "& .MuiAlert-action": { gridColumn: { xs: 2, sm: 3 }, gridRow: { xs: 2, sm: 1 },
    pl: { xs: 0, sm: 2 }, mr: 0, pb: { xs: 1, sm: 0 }, alignItems: "flex-start" },
};

export function ExecutionErrors({ execution, details, spaceId, showLinks = false, monitor, targetInstanceId }) {
  const { t, i18n } = useTranslation("cicdruns");
  const allErrors = [...(execution?.errorDetails || []), ...(details || []), ...(execution?.error && !execution?.errorDetails?.length ? [execution.error] : [])].sort((a, b) => (a.packageId ? 1 : 0) - (b.packageId ? 1 : 0));
  const errors = [...new Map(allErrors.map(error => [JSON.stringify([error.code, error.message, error.runId, error.pid, error.jobName, error.stepName]), error])).values()];
  if (!errors?.length) return null;
  const monitorAction = (error) => {
    if (!monitor) return showLinks ? <CicdMonitorButton spaceId={spaceId} execution={execution} runId={error.runId} /> : null;
    const href = cicdMonitorUrl({ ...monitor, runId: error.runId || monitor.runId });
    if (!href) return null;
    return <Button size="small" color="inherit" component="a" href={href} target="_blank" rel="noopener noreferrer"
      endIcon={<OpenInNewRounded/>}>{t("execution.openMonitor")}</Button>;
  };
  // Pakettransport: fachlicher Text aus dem gemeinsamen Katalog, der Servertext bleibt für Einzelheiten.
  const messageOf = error => (error.code === "PACKAGE_DELETED_UPLOAD_FAILED" ? transportPackageError(error.code, i18n?.language) : null)
    || error.message || error.code || t("execution.errorUnknown");
  const logLink = error => {
    const href = error.code === "PACKAGE_DELETED_UPLOAD_FAILED" ? safeLogUrl(error.logUrl) : null;
    return href ? <div><a href={href} target="_blank" rel="noopener noreferrer">{t("aktion.github")}</a></div> : null;
  };

  const groups = aggregateErrors(errors);
  const language = i18n?.language;

  return <div className="cicd-execution-errors">{groups.map((group, groupIndex) => {
    // Aggregated block: multiple errors of same code+phase
    if (group.length > 1 && group[0].code === "ARTIFACT_FAILED") {
      const phase = extractPhase(group[0]);
      const phaseKey = phase || "unknown";
      const artifactIds = group.map(e => extractArtifactId(e)).filter(Boolean);
      const title = t(`execution.aggregated.ARTIFACT_FAILED.${phaseKey}`, { count: group.length });
      const hint = errorHint(group, language, t);
      // Collect flowIds for runtime-errors fetch (deployment phase only)
      const needsRuntimeErrors = phaseKey === "deployment" && artifactIds.length > 0;
      return <Alert severity="error" key={`agg:${group[0].code}:${phaseKey}:${groupIndex}`} sx={errorPanelLayout}
        action={monitorAction(group[0])}>
        {monitor ? <AlertTitle>{title}</AlertTitle> : <strong>{title}</strong>}
        {hint && <div style={{ fontSize: 12, color: "var(--iio-text-2)", margin: "4px 0 6px", lineHeight: 1.5 }}>{hint}</div>}
        <div style={{ margin: "4px 0" }}>
          {artifactIds.map(id => <div key={id} style={{ fontFamily: "var(--iio-font-mono)", fontSize: 12, color: "var(--iio-text-1)", padding: "1px 0", overflowWrap: "anywhere" }}>{id}</div>)}
        </div>
        {needsRuntimeErrors && targetInstanceId && <div style={{ marginTop: 6, borderTop: "1px solid var(--iio-border-subtle)", paddingTop: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 4 }}>{t("execution.sapErrorsTitle")}</div>
          <RuntimeErrors targetInstanceId={targetInstanceId} flowIds={artifactIds} t={t} />
        </div>}
        <div style={{ fontSize: 11, color: "var(--iio-text-3)" }}>{group[0].code}</div>
      </Alert>;
    }

    // Single ARTIFACT_FAILED with hint
    if (group.length === 1 && group[0].code === "ARTIFACT_FAILED") {
      const error = group[0];
      const phase = extractPhase(error);
      const hint = errorHint(group, language, t);
      const artifactId = extractArtifactId(error);
      const needsRuntimeErrors = phase === "deployment" && artifactId;
      return <Alert severity="error" key={`${error.code}:${error.runId}:${groupIndex}`} sx={errorPanelLayout}
        action={monitorAction(error)}>
        {monitor ? <AlertTitle>{messageOf(error)}</AlertTitle> : <strong>{messageOf(error)}</strong>}
        {hint && <div style={{ fontSize: 12, color: "var(--iio-text-2)", margin: "4px 0 6px", lineHeight: 1.5 }}>{hint}</div>}
        {logLink(error)}
        {(error.jobName || error.stepName || error.packageId || error.pid) && <div>{[error.packageId, error.pid, error.jobName, error.stepName].filter(Boolean).join(" · ")}</div>}
        {error.code && error.code !== error.message && <div>{error.code}</div>}
        {needsRuntimeErrors && targetInstanceId && <div style={{ marginTop: 6, borderTop: "1px solid var(--iio-border-subtle)", paddingTop: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 4 }}>{t("execution.sapErrorsTitle")}</div>
          <RuntimeErrors targetInstanceId={targetInstanceId} flowIds={[artifactId]} t={t} />
        </div>}
      </Alert>;
    }

    // Non-aggregatable group with hint (e.g. PACKAGE_TRANSPORT_APP_UNREACHABLE)
    if (group.length === 1) {
      const error = group[0];
      const hint = errorHint(group, language, t);
      return <Alert severity="error" key={`${error.code}:${error.runId}:${groupIndex}`} sx={errorPanelLayout}
        action={monitorAction(error)}>
        {monitor ? <AlertTitle>{messageOf(error)}</AlertTitle> : <strong>{messageOf(error)}</strong>}
        {hint && <div style={{ fontSize: 12, color: "var(--iio-text-2)", margin: "4px 0 6px", lineHeight: 1.5 }}>{hint}</div>}
        {logLink(error)}
        {(error.jobName || error.stepName || error.packageId || error.pid) && <div>{[error.packageId, error.pid, error.jobName, error.stepName].filter(Boolean).join(" · ")}</div>}
        {error.code && error.code !== error.message && <div>{error.code}</div>}
      </Alert>;
    }

    // Multiple non-ARTIFACT_FAILED errors with same key (shouldn't happen normally, but safe fallback)
    return group.map((error, index) => <Alert severity="error" key={`${error.code}:${error.runId}:${groupIndex}:${index}`} sx={errorPanelLayout}
      action={monitorAction(error)}>
      {monitor ? <AlertTitle>{messageOf(error)}</AlertTitle> : <strong>{messageOf(error)}</strong>}
      {logLink(error)}
      {(error.jobName || error.stepName || error.packageId || error.pid) && <div>{[error.packageId, error.pid, error.jobName, error.stepName].filter(Boolean).join(" · ")}</div>}
      {error.code && error.code !== error.message && <div>{error.code}</div>}
    </Alert>);
  })}</div>;
}
